import { useEffect, useState } from "react";
import { PenLine, Plus, Trash2, Loader2, X, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-[#AEE6D5]/25 bg-[#0B3B3C]/60 px-4 py-3 text-sm text-[#F8FAFC] placeholder-[#F8FAFC]/40 outline-none transition-colors focus:border-[#D4AF37]";

export const VoiceSamples = () => {
  const [samples, setSamples] = useState(null);
  const [editing, setEditing] = useState(null); // {id?, title, content}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const res = await fetch(`${API}/voice-samples`, { credentials: "include" });
      if (res.ok) setSamples(await res.json());
    } catch {
      setSamples([]);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing || editing.title.trim().length < 1 || editing.content.trim().length < 20 || busy) return;
    setBusy(true);
    setError("");
    try {
      const isNew = !editing.id;
      const res = await fetch(isNew ? `${API}/voice-samples` : `${API}/voice-samples/${editing.id}`, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title: editing.title, content: editing.content }),
      });
      if (!res.ok) throw new Error("failed");
      setEditing(null);
      await load();
    } catch {
      setError("Couldn't save the sample — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setError("");
    try {
      const res = await fetch(`${API}/voice-samples/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("failed");
      setSamples((prev) => prev.filter((s) => s.id !== id));
    } catch {
      setError("Couldn't delete the sample — please try again.");
    }
  };

  return (
    <div className="rounded-2xl border border-[#AEE6D5]/12 bg-[#0B3B3C]/50 p-5" data-testid="voice-samples">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-mono text-[0.6rem] uppercase tracking-[0.18em] text-[#AEE6D5]">
            <PenLine className="h-3.5 w-3.5" /> Voice samples
          </p>
          <p className="mt-2 max-w-xl text-xs leading-relaxed text-[#F8FAFC]/60">
            Paste 2–3 pieces of your real writing below. The Scout studies these before every draft so applications
            sound unmistakably like you.
          </p>
        </div>
        {!editing && (
          <button
            onClick={() => setEditing({ title: "", content: "" })}
            data-testid="voice-add-btn"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#D4AF37]/50 px-3 py-1.5 text-xs font-bold text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-[#0A2E2C]"
          >
            <Plus className="h-3.5 w-3.5" /> Add sample
          </button>
        )}
      </div>

      {error && <p className="mt-3 text-sm text-red-300" data-testid="voice-error">{error}</p>}

      <div className="mt-4 space-y-3">
        {samples === null ? (
          <p className="py-4 text-center font-mono text-xs uppercase tracking-[0.18em] text-[#AEE6D5]/60">Loading…</p>
        ) : (
          samples.map((s) =>
            editing?.id === s.id ? null : (
              <div key={s.id} className="rounded-xl border border-[#AEE6D5]/10 bg-[#0A2E2C]/60 p-4" data-testid={`voice-sample-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-bold text-[#F8FAFC]">
                    {s.title}
                    {s.seeded && (
                      <span className="ml-2 rounded-full bg-[#AEE6D5]/10 px-2 py-0.5 text-[0.55rem] font-bold uppercase tracking-[0.08em] text-[#AEE6D5]">
                        starter sample
                      </span>
                    )}
                  </p>
                  <div className="flex shrink-0 gap-2">
                    <button
                      onClick={() => setEditing({ id: s.id, title: s.title, content: s.content })}
                      data-testid={`voice-edit-${s.id}`}
                      className="rounded-full border border-[#AEE6D5]/30 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.08em] text-[#AEE6D5] transition-colors hover:bg-[#AEE6D5]/10"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => remove(s.id)}
                      data-testid={`voice-delete-${s.id}`}
                      className="rounded-full border border-red-400/30 p-1.5 text-red-300 transition-colors hover:bg-red-400/10"
                      aria-label="Delete sample"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-[#F8FAFC]/70">{s.content}</p>
              </div>
            )
          )
        )}

        {editing && (
          <div className="space-y-3 rounded-xl border border-[#D4AF37]/30 bg-[#0A2E2C]/70 p-4" data-testid="voice-editor">
            <input
              value={editing.title}
              onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))}
              placeholder="Sample title — e.g. Keynote description, blog excerpt, bio"
              data-testid="voice-title-input"
              className={inputCls}
            />
            <textarea
              value={editing.content}
              onChange={(e) => setEditing((p) => ({ ...p, content: e.target.value }))}
              rows={6}
              placeholder="Paste your writing here (at least a couple of sentences)…"
              data-testid="voice-content-input"
              className={`${inputCls} resize-y`}
            />
            <div className="flex items-center gap-3">
              <button
                onClick={save}
                disabled={busy || editing.title.trim().length < 1 || editing.content.trim().length < 20}
                data-testid="voice-save-btn"
                className="inline-flex items-center gap-2 rounded-xl bg-[#D4AF37] px-5 py-2.5 text-sm font-bold text-[#0A2E2C] transition-colors hover:bg-[#B99628] disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {editing.id ? "Save changes" : "Save sample"}
              </button>
              <button
                onClick={() => setEditing(null)}
                data-testid="voice-cancel-btn"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#AEE6D5]/30 px-4 py-2 text-xs font-bold text-[#AEE6D5] transition-colors hover:bg-[#AEE6D5]/10"
              >
                <X className="h-3.5 w-3.5" /> Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
